import React, { useEffect, useId, useState } from 'react';
import { Copy, Plus } from 'lucide-react';
import { inboundSchemasApi, effectiveOutputSchema, type SchemaSettings } from '../../services/inboundSchemasApi';
import { contentCatalog, schemaContentPaths, validVariablePath, type ContentSource, type ContentStep, type ContentVariable } from './workflowNotificationContentHelpers';

const categories = ['All', 'Common', 'Alert', 'Notification', 'Integration', 'Global', 'Payload', 'Steps'] as const;

export interface WorkflowVariableBrowserProps {
  workflowIntegrationId?: string | null;
  sourceType: ContentSource;
  workflowSteps: ContentStep[];
  currentStepOrder: number;
  targetLabel: string;
  globalVariableNames?: string[];
  extraPayloadPaths?: string[];
  onSelect: (path: string) => void;
}

export const WorkflowVariableBrowser: React.FC<WorkflowVariableBrowserProps> = ({
  workflowIntegrationId,
  sourceType,
  workflowSteps,
  currentStepOrder,
  targetLabel,
  globalVariableNames = [],
  extraPayloadPaths = [],
  onSelect,
}) => {
  const id = useId();
  const [settings, setSettings] = useState<SchemaSettings | null>(null);
  const [schemaSource, setSchemaSource] = useState<'active' | 'draft'>('active');
  const [schemaState, setSchemaState] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<(typeof categories)[number]>('All');
  const [payloadSource, setPayloadSource] = useState<'all' | 'integration' | 'event'>('all');
  const [manualPath, setManualPath] = useState('');
  const [copyState, setCopyState] = useState('');

  useEffect(() => {
    let cancelled = false;
    setSettings(null);
    setSchemaSource('active');
    setSchemaState(workflowIntegrationId
      ? 'Loading integration schema...'
      : 'No integration association. Payload paths require an example or a known variable path.');
    if (workflowIntegrationId) {
      inboundSchemasApi.list(workflowIntegrationId).then(result => {
        if (cancelled) return;
        setSettings(result.settings);
        setSchemaState(effectiveOutputSchema(result.settings) ? '' : 'No active output schema. Draft paths are design-only.');
      }).catch(() => {
        if (!cancelled) setSchemaState('Integration schema unavailable or permission denied. Known variable paths remain available.');
      });
    }
    return () => { cancelled = true; };
  }, [workflowIntegrationId]);

  const schemaText = schemaSource === 'active' ? effectiveOutputSchema(settings) : settings?.draftOutputSchemaJson;
  let schemaPaths: string[] = [];
  let schemaError = '';
  try { if (schemaText) schemaPaths = schemaContentPaths(schemaText); }
  catch { schemaError = 'Output schema could not be read.'; }

  const catalog = [
    ...contentCatalog(sourceType, workflowSteps, currentStepOrder, [...schemaPaths, ...extraPayloadPaths]),
    ...globalVariableNames.map(name => ({
      path: `global.${name}`,
      category: 'Global' as const,
      note: 'Execution-scoped value shared with awaited sub-workflows and reset for each root run.'
    }))
  ];
  const payloadRoot = payloadSource === 'event' ? 'event.data' : `${sourceType.toLowerCase()}.data`;
  const filtered = catalog.filter(entry =>
    (category === 'All' || category === entry.category) &&
    (category !== 'Payload' || payloadSource === 'all' || entry.path === payloadRoot || entry.path.startsWith(`${payloadRoot}.`)) &&
    entry.path.toLowerCase().includes(search.trim().toLowerCase()));

  const copy = async (path: string) => {
    try {
      await navigator.clipboard.writeText(`{{${path}}}`);
      setCopyState(`Copied ${path}`);
    } catch {
      setCopyState('Copy unavailable. Select a path and insert it into the target.');
    }
  };

  const renderVariable = (entry: ContentVariable) => (
    <div className="workflow-content-variable" key={entry.path}>
      <code title={entry.note}>{`{{${entry.path}}}`}</code>
      <button type="button" className="btn-icon" aria-label={`Copy ${entry.path}`} title={`Copy ${entry.path}`} onClick={() => void copy(entry.path)}><Copy size={16} /></button>
      <button type="button" className="btn-icon" aria-label={`Select ${entry.path}`} title={`Use for ${targetLabel}`} onClick={() => onSelect(entry.path)}><Plus size={16} /></button>
    </div>
  );

  return (
    <details className="workflow-content-picker" open>
      <summary>Variables</summary>
      <label htmlFor={`${id}-search`}>Search variables</label>
      <input id={`${id}-search`} type="search" value={search} onChange={event => setSearch(event.target.value)} />
      <div className="workflow-content-segments" role="group" aria-label="Variable category">
        {categories.map(item => <button type="button" key={item} aria-pressed={category === item} onClick={() => setCategory(item)}>{item}</button>)}
      </div>
      {category === 'Payload' && <label className="workflow-payload-source-label">Payload source
        <select aria-label="Payload source" value={payloadSource} onChange={event => setPayloadSource(event.target.value as typeof payloadSource)}>
          <option value="all">All payload sources</option>
          <option value="integration">{sourceType} integration payload</option>
          <option value="event">Event payload</option>
        </select>
      </label>}
      {settings && <div className="workflow-content-segments" role="group" aria-label="Schema source">
        <button type="button" aria-pressed={schemaSource === 'active'} onClick={() => setSchemaSource('active')}>Active schema</button>
        <button type="button" aria-pressed={schemaSource === 'draft'} onClick={() => setSchemaSource('draft')}>Draft (design only)</button>
      </div>}
      <p className="help-text" role="status">{schemaState || (schemaSource === 'draft' ? 'Draft paths: not available live until activation.' : 'Active declared paths; optional fields may be absent.')}</p>
      {schemaError && <p role="status">{schemaError}</p>}
      <div className="workflow-content-variable-list">
        {filtered.map(renderVariable)}
        {!filtered.length && <p>No matching variables.</p>}
      </div>
      <label htmlFor={`${id}-path`}>Use variable path</label>
      <div className="workflow-content-manual">
        <input id={`${id}-path`} value={manualPath} onChange={event => setManualPath(event.target.value)} placeholder="event.data.normalisedPayload.company.name" />
        <button type="button" className="btn-icon" aria-label={`Use variable for ${targetLabel}`} title={`Use variable for ${targetLabel}`} disabled={!validVariablePath(manualPath.trim())} onClick={() => onSelect(manualPath.trim())}><Plus size={18} /></button>
      </div>
      <p className="help-text" role="status">{copyState}</p>
    </details>
  );
};